import { FC, useEffect, useState, KeyboardEvent } from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { _isNil, _notNil } from '@/littledash.ts';
import { ConsumptionMetric, ConsumptionMetricStatus, ConsumptionType } from '@/model/Consumption.model.ts';
import { useFormContext } from 'react-hook-form@latest';
import Button from '@/components/UI/Button';
import { intakeLabels } from './Consumption.utils';
import { useSelectedCage } from '@/utils/workflow/useWorkflow.tsx';
import ReasonForChangeV7 from '@/components/Shared/ReasonForChangeV7.tsx';

type FieldConfigType = {
  id: string;
  label: string;
  disabled: boolean;
  required: boolean;
  errorMessage: string;
};

interface IntakeInputsFormProps {
  cageMetricStatus: ConsumptionMetricStatus;
  inputInDisabled: boolean;
  inputOutDisabled: boolean;
  lastIn: number | null;
  todayRecord: ConsumptionMetric | undefined;
  type: ConsumptionType;
  handleKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
  editing?: boolean;
}

const validateNumber = (fieldId: string, value: any, lastIn: number | null) => {
  const inputNotPositive = fieldId === 'input_in' && value <= 0;
  const onlyAddingFoodOperation = _isNil(lastIn);
  if (inputNotPositive && onlyAddingFoodOperation) {
    return 'Value must be greater than zero';
  }

  const lessThanZero = value < 0;
  if (lessThanZero) {
    return 'Value cannot be negative';
  }

  if (fieldId !== 'input_in' && _notNil(lastIn)) {
    if (value > lastIn) {
      return 'Value exceeds last recorded in amount';
    }
  }
  return true;
};

export const IntakeInputsForm: FC<IntakeInputsFormProps> = ({
  cageMetricStatus,
  inputInDisabled,
  inputOutDisabled,
  lastIn,
  type,
  handleKeyPress,
  editing = false,
}) => {
  const [commentInputDisabled, setCommentInputDisabled] = useState<boolean>(true);
  const { selectedCage } = useSelectedCage();

  useEffect(() => {
    setCommentInputDisabled(true);
  }, [selectedCage]);

  const {
    register,
    formState: { errors },
  } = useFormContext();

  const fieldLabel: string = intakeLabels[type].label;
  const fieldConfig: Array<FieldConfigType> = [
    {
      id: 'input_out',
      label: `${fieldLabel} Out (g)`,
      disabled: inputOutDisabled,
      required: !inputOutDisabled && (cageMetricStatus == 'initial' || cageMetricStatus == 'continuation'),
      errorMessage: 'This field is required',
    },
  ];
  if (type === 'food') {
    fieldConfig.push({
      id: 'spillage',
      label: 'Spillage (g)',
      disabled: inputOutDisabled,
      required: false,
      errorMessage: '',
    });
  }
  fieldConfig.push({
    id: 'input_in',
    label: `${fieldLabel} In (g)`,
    disabled: inputInDisabled,
    required: !inputInDisabled && cageMetricStatus == 'end',
    errorMessage: 'This field is required',
  });

  const fieldFormInput = (field: FieldConfigType) => (
    <div className={field.id == 'input_out' ? 'pb3 pr2' : 'pb3'} key={field.id}>
      <label>{field.label}</label>
      <input
        type="number"
        step="any"
        id={`${type}.fields.${field.id}`}
        disabled={field.disabled}
        onKeyDown={handleKeyPress}
        style={{ marginBottom: 0 }}
        data-testid={`${fieldLabel}-${field.id}`}
        {...register(`${type}.fields.${field.id}`, {
          required: field.required ? field.errorMessage : false,
          pattern: {
            value: /^[-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?$/,
            message: 'Must be a valid number',
          },
          validate: (value: string | number | null) => validateNumber(field.id, value, lastIn),
        })}
      />
      <ErrorMessage
        errors={errors}
        name={`${type}.fields.${field.id}`}
        render={({ message }) => <p className="f6 red db lh-copy pt1">{message}</p>}
      />
    </div>
  );

  return (
    <>
      <div className="flex justify-between">{fieldConfig.slice(0, 2).map((field) => fieldFormInput(field))}</div>
      {type == 'food' && <div className="flex justify-between w-50">{fieldFormInput(fieldConfig[2])}</div>}
      {editing ? (
        <>
          <div className="pb3">
            <label>Comment</label>
            <input
              type="text"
              step="any"
              disabled={false}
              style={{ marginBottom: 0 }}
              data-testid="edit-input-comment"
              {...register(`${type}.fields.comment`, {
                required: false,
              })}
            />
          </div>
          <ReasonForChangeV7 isDelete={false} useErrorBoarder={false} />
        </>
      ) : (
        <div className="pb3">
          <Button
            className="f6 basier-reg pl0 stateless blue"
            testId={`workflow-${fieldLabel}-comment`}
            onClick={() => setCommentInputDisabled(!commentInputDisabled)}
          >
            Add comment
          </Button>
          {!commentInputDisabled && (
            <div className="mt2">
              <input
                type="text"
                step="any"
                disabled={commentInputDisabled || (inputOutDisabled && inputInDisabled)}
                style={{ marginBottom: 0 }}
                {...register(`${type}.fields.comment`, {
                  required: false,
                })}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};
